import {
  useCallback,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import type { AddressFailure, AddressGroupFilters as Filters } from '../types';
import { DEFAULT_ADDRESS_FILTERS, statusOptions } from './utils';
import {
  useAddressGroups,
  useClusteringStatus,
  useGroupReview,
  useReversalSelection,
  useRunExecutions,
  useRunFailures,
  useRunProcesses,
} from './hooks';
import { useAddressRunColumns } from './address-run-columns';
import { AddressReverseModals } from './address-reverse-modals';
import { AddressRunBanners } from './address-run-banners';
import { AddressRunHeader } from './address-run-header';
import { AddressRunTables } from './address-run-tables';
import { CandidatesHeading } from './address-run-heading';
import { formatScope, refusedFromRuns } from './address-run-helpers';
import styles from '../page.module.scss';

const DEFAULT_PAGE_SIZE = 50;

/**
 * The run's filters, search and pager. A filter or search change takes effect
 * back on page 1. The checkbox toggles apply at once; the numeric range inputs
 * are debounced inside the filter panel. The panel emits only the keys it
 * changed, merged functionally here, so a range emit still pending in the
 * debounce window can't overwrite a checkbox the reviewer ticked meanwhile.
 * @return {object} The filters, search and pager with their change handlers.
 */
function useRunQuery() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_ADDRESS_FILTERS);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const onFiltersChange = useCallback((patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, []);

  const onSearchChange = useCallback((next: string) => {
    setSearch(next);
    setPagination((p) => (p.page === 1 ? p : { ...p, page: 1 }));
  }, []);

  return {
    filters,
    setFilters,
    onFiltersChange,
    search,
    onSearchChange,
    pagination,
    setPagination,
  };
}

/**
 * Drops status picks the filter stops offering: its options change on
 * execution.
 * @param {boolean} executed Whether the run has executed.
 * @param {Function} setFilters Sets the run's filters.
 */
function usePrunedStatuses(
  executed: boolean,
  setFilters: Dispatch<SetStateAction<Filters>>
) {
  const [optionsFor, setOptionsFor] = useState(executed);
  if (optionsFor === executed) return;
  setOptionsFor(executed);
  const allowed = new Set(statusOptions(executed).map((o) => o.value));
  setFilters((f) => {
    if (f.statuses.every((s) => allowed.has(s))) return f;
    return { ...f, statuses: f.statuses.filter((s) => allowed.has(s)) };
  });
}

/**
 * The group and variation review toggles, each firing its POST without the
 * table waiting on it.
 * @param {object} options The page's row and count setters, reload and error.
 * @return {object} Each toggle with its in-flight ids.
 */
function useReviewToggles(options: Parameters<typeof useGroupReview>[0]) {
  const review = useGroupReview(options);
  const { toggleGroups, toggleVariation } = review;
  return {
    ...review,
    toggleGroups: useCallback(
      (ids: number[], value: boolean) => void toggleGroups(ids, value),
      [toggleGroups]
    ),
    toggleVariation: useCallback(
      (ids: number[], value: boolean) => void toggleVariation(ids, value),
      [toggleVariation]
    ),
  };
}

/**
 * Review & Execute for one address clustering run. Every group arrives
 * included; the reviewer records exclusions, then executes.
 * @return {JSX.Element} The page.
 */
export function AddressRunPage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const runId = Number(id);

  const {
    filters,
    setFilters,
    onFiltersChange,
    search,
    onSearchChange,
    pagination,
    setPagination,
  } = useRunQuery();
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Bumped to re-fetch the current page after a combine, reversal or toggle.
  const [refreshToken, setRefreshToken] = useState(0);
  const reloadPage = useCallback(() => setRefreshToken((t) => t + 1), []);
  const [error, setError] = useState<string | null>(null);
  const [refusedCount, setRefusedCount] = useState(0);
  const resetRefused = useCallback(() => setRefusedCount(0), []);

  const { page, pageSize } = pagination;
  const {
    groups,
    setGroups,
    total,
    counts,
    setCounts,
    summary,
    scope,
    loading,
  } = useAddressGroups(
    runId,
    { page, pageSize, filters, search, refreshToken },
    setError
  );
  const { failures, failuresTotal, loadFailures } =
    useRunFailures<AddressFailure>(runId, setError);

  const { executions, loadExecutions } = useRunExecutions(runId, setError);
  // Matches the batch status: a run is executed once it has a merge.
  const executed = executions.length > 0;

  usePrunedStatuses(executed, setFilters);

  useEffect(() => {
    if (executed) void loadFailures();
  }, [executed, loadFailures]);

  const {
    clusterStatus,
    clustering,
    generationFailed: generationStepFailed,
  } = useClusteringStatus(runId, reloadPage, setError);

  const refreshAll = useCallback(() => {
    reloadPage();
    void loadExecutions();
    void loadFailures();
  }, [reloadPage, loadExecutions, loadFailures]);

  // A finished reversal records how many groups it refused (each keeps its
  // status, with its reason); that count rides the run summary, so re-read the
  // executions and show it. The freshly fetched runs are read directly — the
  // `executions` state the refresh sets isn't visible yet.
  const onReverseDone = useCallback(() => {
    reloadPage();
    void loadFailures();
    void loadExecutions().then((runs) =>
      setRefusedCount(refusedFromRuns(runs))
    );
  }, [reloadPage, loadFailures, loadExecutions]);

  const {
    mergeProc,
    mergeStatus,
    reverseProc,
    reverseStatus,
    mergeRunning,
    reversalRunning,
    executeBusy,
    execute,
    reverse,
  } = useRunProcesses({
    runId,
    bulkType: 'addresses',
    setError,
    onMergeDone: refreshAll,
    onReverseDone,
  });

  const {
    groupToReverse,
    setGroupToReverse,
    runToReverse,
    setRunToReverse,
    variationToReverse,
    setVariationToReverse,
    confirmRunReverse,
    confirmReverse,
    confirmVariationReverse,
  } = useReversalSelection(reverse, resetRefused);

  // Always the groups Execute would still act on: a timed-out batch can leave
  // executable groups behind, and the reviewer needs to run the remainder.
  const executableCount =
    (counts.in_scope ?? 0) + (counts.partially_excluded ?? 0);
  // Generation reached a terminal failure (and no merge is draining): the run
  // can't be salvaged, so the page locks and directs the reviewer to start a
  // new one. No retry action by design.
  const generationFailed = generationStepFailed && !mergeRunning;
  // Selection stays editable after an execution — the run can be executed again
  // for its remaining groups, and the server already allows toggles then, so a
  // reviewer can trim or re-include before re-running. Only an in-flight merge,
  // generation, or a failed generation locks it.
  const selectionLocked = mergeRunning || clustering || generationFailed;
  const reverseBlocked = reversalRunning || mergeRunning;

  const { toggleGroups, pendingIds, toggleVariation, pendingMemberIds } =
    useReviewToggles({ setGroups, setCounts, reloadPage, setError });

  const { columns, execColumns, renderVariations } = useAddressRunColumns({
    groups,
    pendingIds,
    pendingMemberIds,
    selectionLocked,
    reverseBlocked,
    toggleGroups,
    toggleVariation,
    onReverseGroup: setGroupToReverse,
    onReverseVariation: setVariationToReverse,
    onReverseRun: setRunToReverse,
  });

  const scopeInfo = formatScope(scope);
  // The candidate count is the run-wide total across every status, read off the
  // run-wide `counts` map — not `total`, which is the filtered/searched row count
  // and would make the heading shrink as a reviewer types in the search box.
  const candidatesTotal = Object.values(counts).reduce((sum, n) => sum + n, 0);

  return (
    <div className={styles.page}>
      <AddressRunHeader
        runId={runId}
        executableCount={executableCount}
        executeDisabled={
          clustering ||
          generationFailed ||
          mergeRunning ||
          reversalRunning ||
          executeBusy ||
          executableCount === 0
        }
        onAllRuns={() => history.push('/admin/bulk_dedupe')}
        onExecute={execute}
      />

      <AddressRunBanners
        error={error}
        onClearError={() => setError(null)}
        clustering={clustering}
        clusterStatus={clusterStatus}
        generationFailed={generationFailed}
        mergeProc={mergeProc}
        mergeStatus={mergeStatus}
        reverseProc={reverseProc}
        reverseStatus={reverseStatus}
        refusedCount={refusedCount}
        onClearRefused={resetRefused}
      />

      <CandidatesHeading
        candidatesTotal={candidatesTotal}
        scopeInfo={scopeInfo}
        summary={summary}
        executions={executions}
      />

      <AddressRunTables
        executed={executed}
        failures={failures}
        failuresTotal={failuresTotal}
        filters={filters}
        onFiltersChange={onFiltersChange}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
        groups={groups}
        columns={columns}
        loading={loading}
        total={total}
        pagination={pagination}
        search={search}
        onPaginationChange={setPagination}
        onSearchChange={onSearchChange}
        renderVariations={renderVariations}
        executions={executions}
        execColumns={execColumns}
      />

      <AddressReverseModals
        runToReverse={runToReverse}
        groupToReverse={groupToReverse}
        variationToReverse={variationToReverse}
        onCloseRun={() => setRunToReverse(null)}
        onConfirmRun={confirmRunReverse}
        onCloseGroup={() => setGroupToReverse(null)}
        onConfirmGroup={confirmReverse}
        onCloseVariation={() => setVariationToReverse(null)}
        onConfirmVariation={confirmVariationReverse}
      />
    </div>
  );
}
